/**
 * SupportComposer — the chat reply box at the foot of the Support chat panel. A labelled textarea + a
 * Send button; Enter sends, Shift+Enter inserts a newline (the chat-app idiom). Disabled when the ticket
 * is closed (a superseded thread is read-only — the server 409s a closed-ticket post; we pre-empt it in
 * the UI and surface the localized notice instead). Token-driven + AA contrast; the textarea carries a
 * real <label> via FormField.
 */
import { useState, type KeyboardEvent } from "react";
import { Button, FormField, Textarea } from "@/ui";
import { useT } from "@/app";

export interface SupportComposerProps {
  /** Send the trimmed message; resolves on success so the box can clear. Rejects → keep the draft. */
  onSend: (contenu: string) => Promise<void>;
  /** True while a send is in flight (disables input + shows the sending label). */
  sending: boolean;
  /** True when the ticket is closed — the composer is read-only and shows the closed notice. */
  disabled?: boolean;
}

export function SupportComposer({ onSend, sending, disabled = false }: SupportComposerProps) {
  const { t } = useT();
  const [draft, setDraft] = useState("");

  const canSend = draft.trim().length > 0 && !sending && !disabled;

  async function submit() {
    const contenu = draft.trim();
    if (!contenu || sending || disabled) return;
    try {
      await onSend(contenu);
      setDraft(""); // clear only on success — a failed send keeps the draft so nothing is lost
    } catch {
      // The parent surfaces the error toast; we keep the draft in the box.
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    // Enter sends; Shift+Enter (or IME composition) inserts a newline.
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void submit();
    }
  }

  if (disabled) {
    return (
      <p
        role="status"
        style={{
          margin: 0,
          padding: "var(--nv-s-3)",
          color: "var(--nv-text-2, var(--nv-slate))",
          fontSize: "var(--nv-fs-caption)",
          textAlign: "center",
        }}
      >
        {t("mgmt.support.chat.closed")}
      </p>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-2)" }}
    >
      <FormField label={t("mgmt.support.composer.label")}>
        {(controlProps) => (
          <Textarea
            {...controlProps}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t("mgmt.support.composer.placeholder")}
            rows={3}
            maxLength={8000}
            disabled={sending}
          />
        )}
      </FormField>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button type="submit" disabled={!canSend}>
          {sending ? t("mgmt.support.composer.sending") : t("mgmt.support.composer.send")}
        </Button>
      </div>
    </form>
  );
}
